import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';
import { Button, Tag } from '@carbon/react';
import { Checkmark, Edit, Delivery, ShoppingBag } from '@carbon/react/icons';
import { OrderStatusEnum, OrderTypeEnum } from '@restavo/shared/enums/order';
import { useMenuItems } from 'src/hooks/useMenuItems';
import { useMoney } from 'src/hooks/useMoney';
import { useUpdateOrdersMutation } from 'src/hooks/useOrders';
import { useTables } from 'src/hooks/useRestaurantTables';
import { OrderEntity } from 'src/services/apis/types';
import { usePOSStore } from 'src/states/pos';
import { clickable } from 'src/utils/a11y';
import { getOrderStatusText, getOrderTypeText } from 'src/utils/order';

interface RecentOrderItemProps {
  order: OrderEntity;
  paidOrderTokens?: string[];
}

const RecentOrderItem = React.memo((props: RecentOrderItemProps) => {
  const { t } = useTranslation();
  const { format } = useMoney();
  const { data: tables } = useTables();
  const { data: menuItems } = useMenuItems();
  const { expandingRecentOrders, toggleExpandingRecentOrders, setEditOrder, setViewingOrder } = usePOSStore(
    (state) => ({
      setEditOrder: state.setEditOrder,
      setViewingOrder: state.setViewingOrder,
      expandingRecentOrders: state.expandingRecentOrders,
      toggleExpandingRecentOrders: state.toggleExpandingRecentOrders,
    }),
    shallow
  );
  const { mutate: updateOrders } = useUpdateOrdersMutation();
  const totalItems = props.order.orderItems.map((item) => item.quantity).reduce((total, quantity) => total + quantity);
  const orderTable = tables?.data?.find((table) => table.id === props.order.tableId);

  const handleChangeOrderStatus = useCallback(
    (status: OrderStatusEnum) => {
      updateOrders({ id: props.order.id, status });
    },
    [updateOrders, props.order.id]
  );

  const paid = props.paidOrderTokens?.includes(props.order.token);

  const orderItemClass = classNames({
    'pos-recent-orders-item': true,
    'pos-recent-orders-item--paid': paid,
  });

  return (
    <div className={orderItemClass}>
      {props.order.type === OrderTypeEnum.DELIVERY && (
        <div className="table" {...clickable(() => setViewingOrder(props.order))}>
          <Delivery size={28} />
        </div>
      )}
      {props.order.type === OrderTypeEnum.TAKEAWAY && (
        <div className="table" {...clickable(() => setViewingOrder(props.order))}>
          <ShoppingBag size={28} />
        </div>
      )}
      {props.order.type === OrderTypeEnum.DINE_IN && (
        <div className="table" {...clickable(() => setViewingOrder(props.order))}>
          {orderTable?.tableNumber}
        </div>
      )}
      <div>
        <div {...clickable(() => setViewingOrder(props.order))}>
          <h3>{t('common.orderItemCount', { count: totalItems })}</h3>
          <ul className="list-horizontal">
            <li>{t(getOrderTypeText(props.order.type))}</li>
            <li>{t(getOrderStatusText(props.order.status))}</li>
          </ul>
        </div>
        <div className="actions">
          <Button
            size={'sm'}
            onClick={() => handleChangeOrderStatus(OrderStatusEnum.READY)}
            renderIcon={Checkmark}
            kind={props.order.status === OrderStatusEnum.READY ? 'primary' : 'tertiary'}
          />
          /
          {/* Disabled until the menu loads: editing rebuilds the cart from it. And `?.`, not
              `menuItems!.data` — React Compiler memoized this button on an unguarded
              `menuItems.data`, so a card rendered before the menu arrived threw. */}
          <Button
            size={'sm'}
            disabled={!menuItems}
            onClick={() => {
              if (!menuItems) {
                return;
              }
              if (expandingRecentOrders) {
                toggleExpandingRecentOrders();
              }
              setEditOrder(props.order, menuItems.data);
            }}
            renderIcon={Edit}
            kind={'tertiary'}
          />
        </div>
      </div>
      <Tag className="pos-recent-orders-item-status" type="red" title="Clear Filter">
        {format(props.order.grandTotal)}
      </Tag>
    </div>
  );
});

export default RecentOrderItem;
